const PERFORMANCE_BUFFER_KEY = '__nivoraPerformance';
const MAX_PERFORMANCE_ENTRIES = 200;
const TELEMETRY_CONFIG_KEY = '__nivoraPerformanceConfig';
const MAX_RESOURCE_ENTRIES = 300;
const SENSITIVE_KEY = /(authorization|cookie|secret|token|password|cpf|cnpj|email|payload|qr|certificate|credential)/i;

let telemetryConfig = null;
let diagnosticsCleanup = [];
let firstInteractionRecorded = false;
let correlationSequence = 0;

function environmentValue(name) {
  try {
    return typeof process !== 'undefined' ? process.env?.[name] : undefined;
  } catch (_error) {
    return undefined;
  }
}

function defaultEnabled() {
  const browserOverride = typeof window !== 'undefined'
    ? window[TELEMETRY_CONFIG_KEY]?.enabled
    : undefined;
  if (typeof browserOverride === 'boolean') return browserOverride;
  const configured = environmentValue('REACT_APP_NIVORA_OBSERVABILITY');
  if (configured !== undefined) {
    return !['0', 'false', 'off', 'disabled'].includes(String(configured).toLowerCase());
  }
  return environmentValue('NODE_ENV') !== 'production';
}

function getConfig() {
  if (!telemetryConfig) telemetryConfig = { enabled: defaultEnabled(), context: {} };
  return telemetryConfig;
}

function sanitiseValue(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;
  return value.length > 160 ? value.slice(0, 160) : value;
}

function sanitiseMetadata(metadata = {}) {
  return Object.entries(metadata).reduce((result, [key, value]) => {
    if (SENSITIVE_KEY.test(String(key))) return result;
    const safe = sanitiseValue(value);
    if (safe !== undefined) result[key] = safe;
    return result;
  }, {});
}

function safePath(value) {
  try {
    const origin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';
    const parsed = new URL(String(value || ''), origin);
    return parsed.pathname.slice(0, 240) || '/';
  } catch (_error) {
    return String(value || '').split(/[?#]/, 1)[0].slice(0, 240) || '/';
  }
}

function runtimePerformance() {
  return typeof window !== 'undefined' && window.performance
    ? window.performance
    : null;
}

function now() {
  return runtimePerformance()?.now?.() ?? Date.now();
}

function publish(entry) {
  if (!getConfig().enabled) return null;
  if (typeof window === 'undefined') return entry;

  const current = Array.isArray(window[PERFORMANCE_BUFFER_KEY])
    ? window[PERFORMANCE_BUFFER_KEY]
    : [];
  current.push(Object.freeze(entry));
  if (current.length > MAX_PERFORMANCE_ENTRIES) {
    current.splice(0, current.length - MAX_PERFORMANCE_ENTRIES);
  }
  window[PERFORMANCE_BUFFER_KEY] = current;

  if (typeof window.dispatchEvent === 'function'
      && typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new window.CustomEvent('nivora:performance', {
      detail: entry
    }));
  }
  return entry;
}

export function recordPerformance(name, {
  startedAt,
  finishedAt = now(),
  status = 'ok',
  ...metadata
} = {}) {
  if (!getConfig().enabled) return null;
  const start = Number.isFinite(Number(startedAt)) ? Number(startedAt) : finishedAt;
  return publish({
    name: String(name || 'unknown'),
    durationMs: Math.max(0, Number(finishedAt) - start),
    status: String(status || 'ok'),
    capturedAt: new Date().toISOString(),
    ...sanitiseMetadata(metadata),
    ...sanitiseMetadata(getConfig().context)
  });
}

export function configurePerformanceTelemetry({ enabled, context = {} } = {}) {
  const current = getConfig();
  if (typeof enabled === 'boolean') current.enabled = enabled;
  current.context = { ...current.context, ...sanitiseMetadata(context) };
  if (typeof window !== 'undefined') {
    window[TELEMETRY_CONFIG_KEY] = { enabled: current.enabled };
  }
  return { enabled: current.enabled, context: { ...current.context } };
}

export function isPerformanceTelemetryEnabled() {
  return Boolean(getConfig().enabled);
}

export function setPerformanceContext(context = {}) {
  return configurePerformanceTelemetry({ context });
}

export function markPerformance(name, metadata = {}) {
  const timestamp = now();
  try {
    runtimePerformance()?.mark?.(`nivora:${String(name || 'unknown')}`);
  } catch (_error) {
    // Performance.mark is optional and must never block the application.
  }
  return recordPerformance(name, { startedAt: timestamp, finishedAt: timestamp, ...metadata });
}

export function createCorrelationId(prefix = 'nivora') {
  correlationSequence += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${correlationSequence.toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function resourceSummary(entry) {
  return {
    name: safePath(entry.name),
    initiatorType: String(entry.initiatorType || 'unknown').slice(0, 40),
    durationMs: Number.isFinite(entry.duration) ? Math.max(0, entry.duration) : null,
    transferSize: Number.isFinite(entry.transferSize) ? entry.transferSize : null,
    encodedBodySize: Number.isFinite(entry.encodedBodySize) ? entry.encodedBodySize : null,
    decodedBodySize: Number.isFinite(entry.decodedBodySize) ? entry.decodedBodySize : null
  };
}

function browserContext() {
  const online = typeof navigator === 'undefined' ? null : navigator.onLine !== false;
  let route = '/';
  if (typeof window !== 'undefined') route = safePath(window.location?.pathname || '/');
  return {
    route,
    online,
    coldWarm: typeof sessionStorage !== 'undefined' && sessionStorage.getItem('nivora:boot-seen')
      ? 'warm'
      : 'cold',
    version: environmentValue('REACT_APP_VERSION') || environmentValue('npm_package_version') || 'unknown'
  };
}

export function installPerformanceDiagnostics() {
  if (!isPerformanceTelemetryEnabled() || typeof window === 'undefined') return () => {};
  diagnosticsCleanup.forEach((cleanup) => cleanup());
  diagnosticsCleanup = [];
  firstInteractionRecorded = false;

  const onInteraction = (event) => {
    if (firstInteractionRecorded) return;
    firstInteractionRecorded = true;
    markPerformance('first_user_interaction', { inputType: String(event?.type || 'unknown') });
  };
  ['pointerdown', 'keydown', 'touchstart'].forEach((type) => window.addEventListener(type, onInteraction, { once: false, passive: true }));
  diagnosticsCleanup.push(() => ['pointerdown', 'keydown', 'touchstart'].forEach((type) => window.removeEventListener(type, onInteraction)));

  const onError = (event) => {
    recordPerformance('resource_or_runtime_error', {
      status: 'error',
      errorCode: 'browser_error',
      source: safePath(event?.filename || event?.target?.src || event?.target?.href || '')
    });
  };
  const onRejection = () => recordPerformance('resource_or_runtime_error', { status: 'error', errorCode: 'unhandled_rejection' });
  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection);
  diagnosticsCleanup.push(() => {
    window.removeEventListener('error', onError, true);
    window.removeEventListener('unhandledrejection', onRejection);
  });

  if (typeof window.PerformanceObserver === 'function') {
    try {
      const observer = new window.PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'longtask') {
            recordPerformance('long_task', { durationMs: entry.duration, source: entry.name || 'unknown' });
          }
        });
      });
      observer.observe({ type: 'longtask', buffered: true });
      diagnosticsCleanup.push(() => observer.disconnect());
    } catch (_error) {
      // Long-task support is optional across browsers.
    }
  }
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('nivora:boot-seen', '1');
  } catch (_error) { /* optional */ }
  setPerformanceContext(browserContext());
  return () => {
    diagnosticsCleanup.forEach((cleanup) => cleanup());
    diagnosticsCleanup = [];
  };
}

function navigationSummary() {
  const navigation = runtimePerformance()?.getEntriesByType?.('navigation')?.[0];
  if (!navigation) return null;
  return {
    type: navigation.type || 'unknown',
    durationMs: navigation.duration,
    domContentLoadedMs: navigation.domContentLoadedEventEnd,
    loadEventMs: navigation.loadEventEnd,
    responseEndMs: navigation.responseEnd,
    transferSize: navigation.transferSize ?? null,
    encodedBodySize: navigation.encodedBodySize ?? null,
    decodedBodySize: navigation.decodedBodySize ?? null
  };
}

export function getPerformanceReport() {
  const performanceObject = runtimePerformance();
  const resources = performanceObject?.getEntriesByType?.('resource') || [];
  const memory = performanceObject?.memory && {
    jsHeapSizeLimit: performanceObject.memory.jsHeapSizeLimit,
    totalJSHeapSize: performanceObject.memory.totalJSHeapSize,
    usedJSHeapSize: performanceObject.memory.usedJSHeapSize
  };
  return {
    schema: 'nivora.observability.v1',
    exportedAt: new Date().toISOString(),
    enabled: isPerformanceTelemetryEnabled(),
    context: { ...getConfig().context, ...browserContext() },
    entries: getPerformanceEntries(),
    navigation: navigationSummary(),
    resources: resources.slice(-MAX_RESOURCE_ENTRIES).map(resourceSummary),
    requestCount: resources.length,
    jsChunks: resources.filter((entry) => /\.chunk\.js$|main\..+\.js$/.test(String(entry.name || ''))).length,
    wasmCount: resources.filter((entry) => String(entry.name || '').endsWith('.wasm')).length,
    memory: memory || null,
    limitations: [
      'Métricas de banco, queries e worker dependem da instrumentação server-side correspondente.',
      'Tamanho local da base não é inferido do conteúdo; somente volume informado explicitamente pelo bridge é aceito.'
    ]
  };
}

export function exportPerformanceReport() {
  return JSON.stringify(getPerformanceReport());
}

export function startPerformanceSpan(name, metadata = {}) {
  const startedAt = now();
  return (extra = {}) => recordPerformance(name, {
    ...metadata,
    ...extra,
    startedAt
  });
}

export function measurePerformance(name, operation, metadata = {}) {
  const finish = startPerformanceSpan(name, metadata);
  return Promise.resolve()
    .then(operation)
    .then((value) => {
      finish();
      return value;
    })
    .catch((error) => {
      finish({
        status: 'error',
        errorCode: String(error?.code || error?.status || 'unknown')
      });
      throw error;
    });
}

export function getPerformanceEntries() {
  if (typeof window === 'undefined' || !Array.isArray(window[PERFORMANCE_BUFFER_KEY])) {
    return [];
  }
  return [...window[PERFORMANCE_BUFFER_KEY]];
}
